import React, { useState, useEffect, useCallback } from 'react'
import { Table, Text, Skeleton, Pagination, Group, Box, ActionIcon } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { IconX } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { getStockMovements } from '../../../services/inventory'

const shipmentMethodList = [
    { value: 'air', name: 'Air Cargo' },
    { value: 'air_dtd', name: 'Air Cargo Door To Door' },
    { value: 'ocean', name: 'Ocean Cargo' },
    { value: 'ocean_dtd', name: 'Ocean Cargo Door To Door' },
    { value: 'official_trucking', name: 'Trucking' },
    { value: 'unofficial_trucking', name: 'Trucking Unofficial' },
    { value: 'air_courier', name: 'Air Courier' },
]

const headerLabels = [
    'Movement Date', 'Type', 'Warehouse', 'Fulfillment Number',
    'Order Id', 'Product Name', 'Created By', 'Remarks', 'Delivery Method', 'In', 'Out'
]

const MovementHistory = ({ inventoryId, productName, onGoToUser }) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 })

    const fetchData = useCallback(async () => {
        if (!inventoryId) return
        setLoading(true)
        try {
            const res = await getStockMovements({ page: pagination.page, limit: pagination.limit, inventoryId })
            const movements = res?.data?.stockMovements
            if (movements) {
                const mapped = (movements.data || []).map(m => {
                    const findItem = (m.items || []).find(e => Number(e.id) === Number(inventoryId))
                    const pName = findItem?.productName || ''
                    const qty = findItem?.stockMovementPivot?.quantity ? Number(findItem.stockMovementPivot.quantity) : 0
                    const shipMethod = shipmentMethodList.find(e => e.value === m.deliveryMethod)

                    return {
                        id: Number(m.id),
                        fulfillmentNumber: m.fulfillmentNumber,
                        type: m.type,
                        movementDate: m.movementDate,
                        warehouseName: m.warehouse?.name || '',
                        orderId: m.orderId,
                        remarks: m.remarks,
                        deliveryMethod: shipMethod ? shipMethod.name : m.deliveryMethod,
                        createdAt: m.createdAt,
                        productName: pName,
                        quantity: qty,
                        creator: m.creator,
                    }
                })
                setData(mapped)
                const pi = movements.paginatorInfo
                setPagination(prev => ({
                    ...prev,
                    page: pi.currentPage,
                    total: Math.ceil(pi.total / prev.limit)
                }))
            }
        } catch (error) {
            notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line
    }, [inventoryId, pagination.page])

    useEffect(() => { fetchData() }, [fetchData])

    const changePage = (page) => setPagination(prev => ({ ...prev, page }))

    if (loading) {
        return (
            <Box>
                <Text mb="md">Product: {productName}</Text>
                <Table striped>
                    <Table.Thead>
                        <Table.Tr>
                            {headerLabels.map((h, i) => <Table.Th key={i}>{h}</Table.Th>)}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {Array.from({ length: 10 }).map((_, i) => (
                            <Table.Tr key={i}>
                                {headerLabels.map((_, j) => <Table.Td key={j}><Skeleton height={16} radius="sm" /></Table.Td>)}
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Box>
        )
    }

    return (
        <Box>
            <Text mb="md">Product: {productName}</Text>
            <div style={{ overflowX: 'auto' }}>
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            {headerLabels.map((h, i) => <Table.Th key={i}>{h}</Table.Th>)}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.map((row) => (
                            <Table.Tr key={row.id}>
                                <Table.Td><Text size="xs">{dayjs(row.movementDate).format('DD MMMM YYYY, HH:mm')}</Text></Table.Td>
                                <Table.Td>
                                    <Text size="xs" fw={700} tt="capitalize" c={row.type === 'inbound' ? 'green' : 'red'}>
                                        {row.type}
                                    </Text>
                                </Table.Td>
                                <Table.Td><Text size="xs">{row.warehouseName}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={700} c="blue">{row.fulfillmentNumber}</Text></Table.Td>
                                <Table.Td ta="center">
                                    {row.orderId ? <Text size="xs"># <Text component="span" c="red">{row.orderId}</Text></Text> : <Text size="xs">-</Text>}
                                </Table.Td>
                                <Table.Td><Text size="xs">{row.productName}</Text></Table.Td>
                                <Table.Td>
                                    {row.creator?.id ? (
                                        <div>
                                            <Group gap={4} justify="center">
                                                <Text size="xs" c="blue">{row.creator.name}</Text>
                                                <ActionIcon variant="subtle" size="xs" onClick={() => onGoToUser(row.creator.id)}>
                                                    <IconExternalLink size={12} />
                                                </ActionIcon>
                                            </Group>
                                            <Text size="10px" c="dimmed" ta="center">
                                                {dayjs(row.createdAt).format('DD/MMMM/YYYY')} {dayjs(row.createdAt).format('HH:mm:ss')}
                                            </Text>
                                        </div>
                                    ) : (
                                        <div>
                                            <Text size="xs" c="blue" ta="center">Unknown User</Text>
                                            <Text size="10px" c="dimmed" ta="center">
                                                {dayjs(row.createdAt).format('DD/MMMM/YYYY')} {dayjs(row.createdAt).format('HH:mm:ss')}
                                            </Text>
                                        </div>
                                    )}
                                </Table.Td>
                                <Table.Td><Text size="xs">{row.remarks}</Text></Table.Td>
                                <Table.Td><Text size="xs">{row.deliveryMethod}</Text></Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="green" fw={600}>{row.type === 'inbound' ? `+${row.quantity}` : ''}</Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="red" fw={600}>{row.type === 'outbound' ? `-${row.quantity}` : ''}</Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </div>
            <Group justify="space-between" mt="md">
                <Text c="dimmed" size="sm">Page {pagination.page}</Text>
                <Pagination size="sm" value={pagination.page} total={pagination.total} onChange={changePage} />
            </Group>
        </Box>
    )
}

export default MovementHistory
